import { useFocusEffect } from '@react-navigation/native';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
    ActivityIndicator,
    FlatList,
    Image,
    KeyboardAvoidingView,
    Platform,
    RefreshControl,
    StyleSheet,
    Text,
    TextInput,
    TouchableOpacity,
    View,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { Ionicons } from '@expo/vector-icons';
import * as Haptics from 'expo-haptics';
import * as ImagePicker from 'expo-image-picker';
import * as ImageManipulator from 'expo-image-manipulator';
import { AuthContext } from '../context/AuthContext';
import api from '../services/api';
import { showAlert } from '../services/confirm';
import Colors from '../constants/Colors';

const theme = Colors.light;
const { spacing, radius } = Colors;
const CHAT_POLL_INTERVAL = 1500;
const REQUEST_POLL_INTERVAL = 2500;
const FULL_SYNC_EVERY = 20;
const COMPOSER_HEIGHT = 42;
const MESSAGE_TYPE = {
    TEXT: 'text',
    IMAGE: 'image',
};
const MAX_IMAGE_DATA_URL_LENGTH = 2 * 1024 * 1024;

const WECHAT = {
    page: '#EDEDED',
    panel: '#EDEDED',
    title: '#111111',
    subtitle: '#7A7A7A',
    emptyCard: '#FFFFFF',
    emptyText: '#222222',
    emptySubText: '#7A7A7A',
    otherBubble: '#FFFFFF',
    mineBubble: '#95EC69',
    otherText: '#111111',
    mineText: '#111111',
    time: '#8A8A8A',
    inputBar: '#F7F7F7',
    inputBg: '#FFFFFF',
    inputBorder: '#D7D7D7',
    sendBtn: '#07C160',
    sendBtnText: '#FFFFFF',
    divider: '#DADADA',
};

function formatTime(value) {
    if (!value) return '刚刚';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '刚刚';
    return date.toLocaleString('zh-CN', { hour12: false });
}

function getChatErrorMessage(error) {
    const status = error?.response?.status;
    const serverMessage = error?.response?.data?.message;

    if (serverMessage) return serverMessage;
    if (status === 401) return '登录状态已失效，请重新登录。';
    if (status === 404) return '聊天接口暂不可用，请稍后重试。';
    if (status === 0) return '网络连接异常，请检查网络后重试。';
    return error?.message || '请求失败，请稍后重试。';
}

function getRequestErrorMessage(error) {
    return error?.response?.data?.message || error?.message || '处理请求失败，请稍后重试。';
}

function getRequestCopy(request) {
    if (!request) return null;
    const counterpart = request.counterpartName || '对方';

    if (request.requestType === 'relationship') {
        const statusLabel = request.targetStatusLabel || '新模式';
        if (request.isResponder) {
            return {
                title: '关系模式请求',
                description: `${counterpart} 请求切换为“${statusLabel}”。`,
                confirmText: '同意',
                rejectText: '拒绝',
            };
        }
        return {
            title: '关系模式请求已发送',
            description: `你已向 ${counterpart} 发起模式切换请求，等待对方确认。`,
        };
    }

    if (request.requestType === 'unpair') {
        if (request.isResponder) {
            return {
                title: '解除配对请求',
                description: `${counterpart} 请求解除配对。`,
                confirmText: '同意解除',
                rejectText: '拒绝',
            };
        }
        return {
            title: '解除配对请求已发送',
            description: `你已向 ${counterpart} 发起解除配对请求，等待对方确认。`,
        };
    }

    if (request.isResponder) {
        return {
            title: '配对请求',
            description: `${counterpart} 向你发起了配对请求。`,
            confirmText: '同意配对',
            rejectText: '拒绝',
        };
    }

    return {
        title: '配对请求已发送',
        description: `你已向 ${counterpart} 发起配对请求，等待对方确认。`,
    };
}

function normalizeChatList(list) {
    return (Array.isArray(list) ? list : [])
        .map((item, index) => {
            const messageTypeRaw = item.messageType ?? item.message_type ?? MESSAGE_TYPE.TEXT;
            const messageType = messageTypeRaw === MESSAGE_TYPE.IMAGE ? MESSAGE_TYPE.IMAGE : MESSAGE_TYPE.TEXT;
            const imageData = typeof item.imageData === 'string'
                ? item.imageData
                : (typeof item.image_data === 'string' ? item.image_data : '');
            const content = (item.content ?? '').toString();

            return {
                id: item.id || `${index}-${item.createdAt || item.created_at || ''}`,
                senderId: item.senderId ?? item.sender_id,
                senderName: item.senderName ?? item.sender_name ?? '对方',
                messageType,
                content,
                imageData,
                createdAt: item.createdAt ?? item.created_at,
                readAt: item.readAt ?? item.read_at ?? null,
                isRead: item.isRead ?? item.is_read ?? Boolean(item.readAt ?? item.read_at),
            };
        })
        .filter((item) => (item.messageType === MESSAGE_TYPE.IMAGE ? Boolean(item.imageData) : Boolean(item.content)));
}

function mergeChatMessages(previous, incoming) {
    const messageMap = new Map();
    [...previous, ...incoming].forEach((message) => {
        messageMap.set(String(message.id), message);
    });

    return Array.from(messageMap.values()).sort(
        (left, right) => new Date(left.createdAt).getTime() - new Date(right.createdAt).getTime()
    );
}

function getMaxMessageId(list) {
    return (Array.isArray(list) ? list : []).reduce((maxId, item) => {
        const numericId = Number(item?.id || 0);
        return Number.isFinite(numericId) ? Math.max(maxId, numericId) : maxId;
    }, 0);
}

export default function MessagesScreen() {
    const { user, refreshPartner } = useContext(AuthContext);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [chatMessages, setChatMessages] = useState([]);
    const [chatAvailable, setChatAvailable] = useState(true);
    const [chatErrorMessage, setChatErrorMessage] = useState('');
    const [chatInput, setChatInput] = useState('');
    const [sending, setSending] = useState(false);
    const [actionRequest, setActionRequest] = useState(null);
    const [handlingActionRequest, setHandlingActionRequest] = useState(false);

    const lastMessageIdRef = useRef(0);
    const silentPollCountRef = useRef(0);
    const chatMessagesRef = useRef([]);

    useEffect(() => {
        chatMessagesRef.current = chatMessages;
    }, [chatMessages]);

    const loadChat = useCallback(async (mode = 'silent') => {
        if (!user?.partnerId) {
            setChatMessages([]);
            setChatAvailable(true);
            setChatErrorMessage('');
            setLoading(false);
            setRefreshing(false);
            lastMessageIdRef.current = 0;
            silentPollCountRef.current = 0;
            return;
        }

        try {
            if (mode === 'manual') {
                setRefreshing(true);
            } else if (mode === 'initial') {
                setLoading(true);
            }

            if (mode === 'silent') {
                silentPollCountRef.current += 1;
            }

            const hasPendingOutgoingReadReceipts = chatMessagesRef.current.some(
                (message) => message.senderId === user?.id && !message.isRead
            );

            const shouldUseDeltaSync =
                mode === 'silent'
                && !hasPendingOutgoingReadReceipts
                && lastMessageIdRef.current > 0
                && (silentPollCountRef.current % FULL_SYNC_EVERY !== 0);

            let hasSynced = false;
            if (shouldUseDeltaSync) {
                try {
                    const deltaResponse = await api.get(
                        `/chat/updates?sinceId=${lastMessageIdRef.current}&limit=80`
                    );
                    const normalizedDelta = normalizeChatList(deltaResponse?.data?.messages);
                    if (normalizedDelta.length > 0) {
                        setChatMessages((previous) => mergeChatMessages(previous, normalizedDelta));
                        const responseMaxId = Number(deltaResponse?.data?.maxId || 0);
                        lastMessageIdRef.current = Math.max(
                            lastMessageIdRef.current,
                            getMaxMessageId(normalizedDelta),
                            Number.isFinite(responseMaxId) ? responseMaxId : 0
                        );
                    }
                    hasSynced = true;
                } catch (deltaError) {
                    if (deltaError?.response?.status !== 404) {
                        throw deltaError;
                    }
                }
            }

            if (!hasSynced) {
                const fullResponse = await api.get('/chat/history?limit=120');
                const normalizedFull = normalizeChatList(fullResponse?.data);
                setChatMessages(normalizedFull);
                lastMessageIdRef.current = getMaxMessageId(normalizedFull);
            }

            setChatAvailable(true);
            setChatErrorMessage('');
        } catch (error) {
            const message = getChatErrorMessage(error);
            if (mode === 'initial' || mode === 'manual') {
                setChatMessages([]);
                setChatAvailable(false);
            }
            setChatErrorMessage(message);
        } finally {
            if (mode === 'initial') {
                setLoading(false);
            }
            if (mode === 'manual') {
                setRefreshing(false);
            }
        }
    }, [user?.id, user?.partnerId]);

    const loadActionRequest = useCallback(async () => {
        try {
            const [pairResponse, relationshipResponse] = await Promise.all([
                api.get('/user/pair/status'),
                api.get('/user/relationship/status'),
            ]);

            const pairRequest = pairResponse?.data?.request || null;
            const relationshipRequest = relationshipResponse?.data?.request || null;
            setActionRequest(pairRequest || relationshipRequest || null);
        } catch (error) {
            setActionRequest(null);
        }
    }, []);

    const handleActionRequestResponse = useCallback(async (action) => {
        if (!actionRequest?.id || !actionRequest?.isResponder || handlingActionRequest) return;

        const endpoint = actionRequest.requestType === 'relationship'
            ? '/user/relationship/respond'
            : '/user/pair/respond';

        try {
            setHandlingActionRequest(true);
            const response = await api.post(endpoint, {
                requestId: actionRequest.id,
                action,
            });

            await Promise.all([
                refreshPartner(),
                loadActionRequest(),
                loadChat('silent'),
            ]);

            await Haptics.notificationAsync(Haptics.NotificationFeedbackType.Success);
            showAlert('已处理', response?.data?.message || '请求处理成功。');
        } catch (error) {
            await Haptics.notificationAsync(Haptics.NotificationFeedbackType.Error);
            showAlert('处理失败', getRequestErrorMessage(error));
        } finally {
            setHandlingActionRequest(false);
        }
    }, [actionRequest, handlingActionRequest, loadActionRequest, loadChat, refreshPartner]);

    const sendMessage = useCallback(async ({ type = MESSAGE_TYPE.TEXT, content = '', imageData = '' }) => {
        if (!user?.partnerId) {
            showAlert('不可用', '请先完成配对后再聊天。');
            return false;
        }

        if (!chatAvailable) {
            showAlert('发送失败', chatErrorMessage || '聊天服务暂不可用。');
            return false;
        }

        try {
            setSending(true);
            const response = await api.post('/chat/send', { type, content, imageData });
            const serverChat = response?.data?.chat;
            const normalizedCreated = normalizeChatList(serverChat ? [serverChat] : []);

            if (normalizedCreated.length > 0) {
                const createdMessage = normalizedCreated[0];
                setChatMessages((previous) => mergeChatMessages(previous, [createdMessage]));
                lastMessageIdRef.current = Math.max(
                    lastMessageIdRef.current,
                    getMaxMessageId([createdMessage])
                );
            } else {
                await loadChat('silent');
            }

            if (type === MESSAGE_TYPE.TEXT) {
                setChatInput('');
            }
            await Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
            return true;
        } catch (error) {
            showAlert('发送失败', getChatErrorMessage(error));
            return false;
        } finally {
            setSending(false);
        }
    }, [chatAvailable, chatErrorMessage, loadChat, user?.partnerId]);

    useFocusEffect(
        useCallback(() => {
            loadChat('initial');
            loadActionRequest();

            const chatTimer = setInterval(() => loadChat('silent'), CHAT_POLL_INTERVAL);
            const requestTimer = setInterval(loadActionRequest, REQUEST_POLL_INTERVAL);

            return () => {
                clearInterval(chatTimer);
                clearInterval(requestTimer);
            };
        }, [loadActionRequest, loadChat])
    );

    const handleSendChat = async () => {
        const content = chatInput.trim();
        if (!content) return;
        await sendMessage({ type: MESSAGE_TYPE.TEXT, content });
    };

    const handlePickImage = async () => {
        if (sending) return;

        const permission = await ImagePicker.requestMediaLibraryPermissionsAsync();
        if (permission.status !== 'granted') {
            showAlert('提示', '请先允许访问相册后再发送图片。');
            return;
        }

        try {
            const result = await ImagePicker.launchImageLibraryAsync({
                mediaTypes: ImagePicker.MediaTypeOptions.Images,
                allowsEditing: false,
                quality: 1,
            });

            if (result.canceled) return;
            const asset = result.assets?.[0];
            if (!asset?.uri) {
                showAlert('提示', '未读取到图片。');
                return;
            }

            const width = Number(asset.width || 0);
            const manipActions = [];
            if (width > 1080) {
                manipActions.push({ resize: { width: 1080 } });
            }

            const processed = await ImageManipulator.manipulateAsync(
                asset.uri,
                manipActions,
                {
                    compress: 0.75,
                    format: ImageManipulator.SaveFormat.JPEG,
                    base64: true,
                }
            );

            if (!processed?.base64) {
                showAlert('发送失败', '图片处理失败，请重试。');
                return;
            }

            const imageData = `data:image/jpeg;base64,${processed.base64}`;
            if (imageData.length > MAX_IMAGE_DATA_URL_LENGTH) {
                showAlert('提示', '图片过大，请选择更小的图片。');
                return;
            }

            await sendMessage({ type: MESSAGE_TYPE.IMAGE, imageData });
        } catch (error) {
            showAlert('发送失败', error?.message || '选择图片失败，请重试。');
        }
    };

    const chatSubtitle = useMemo(() => {
        if (!user?.partnerId) return '未配对，当前无法聊天。';
        const partnerDisplayName = user?.partnerNickname || user?.partnerUsername;
        return partnerDisplayName
            ? `与 ${partnerDisplayName} 实时聊天`
            : '与伴侣实时聊天';
    }, [user?.partnerId, user?.partnerNickname, user?.partnerUsername]);

    const actionRequestCopy = useMemo(
        () => getRequestCopy(actionRequest),
        [actionRequest]
    );

    if (loading) {
        return (
            <View style={styles.loadingContainer}>
                <ActivityIndicator size="large" color={WECHAT.sendBtn} />
            </View>
        );
    }

    return (
        <SafeAreaView style={styles.container} edges={['top']}>
            <KeyboardAvoidingView
                style={styles.keyboardWrap}
                behavior={Platform.OS === 'ios' ? 'padding' : undefined}
                keyboardVerticalOffset={Platform.OS === 'ios' ? 6 : 0}
            >
                <View style={styles.content}>
                    <View style={styles.header}>
                        <Text style={styles.title}>消息</Text>
                        <Text style={styles.subtitle}>{chatSubtitle}</Text>
                    </View>

                    {actionRequestCopy && (
                        <View style={styles.requestCard}>
                            <Text style={styles.requestTitle}>{actionRequestCopy.title}</Text>
                            <Text style={styles.requestDesc}>{actionRequestCopy.description}</Text>
                            {actionRequest?.isResponder ? (
                                <View style={styles.requestActionRow}>
                                    <TouchableOpacity
                                        style={[styles.requestBtn, styles.requestRejectBtn]}
                                        onPress={() => handleActionRequestResponse('reject')}
                                        disabled={handlingActionRequest}
                                    >
                                        <Text style={styles.requestRejectText}>
                                            {actionRequestCopy.rejectText || '拒绝'}
                                        </Text>
                                    </TouchableOpacity>
                                    <TouchableOpacity
                                        style={[styles.requestBtn, styles.requestConfirmBtn]}
                                        onPress={() => handleActionRequestResponse('confirm')}
                                        disabled={handlingActionRequest}
                                    >
                                        <Text style={styles.requestConfirmText}>
                                            {handlingActionRequest
                                                ? '处理中'
                                                : (actionRequestCopy.confirmText || '同意')}
                                        </Text>
                                    </TouchableOpacity>
                                </View>
                            ) : (
                                <Text style={styles.requestPending}>等待对方处理。</Text>
                            )}
                        </View>
                    )}

                    {!user?.partnerId ? (
                        <View style={styles.emptyCard}>
                            <Text style={styles.emptyTitle}>尚未配对</Text>
                            <Text style={styles.emptySubtitle}>
                                请先到首页查看自己的配对码。你也可以在这里处理配对请求。
                            </Text>
                        </View>
                    ) : !chatAvailable ? (
                        <View style={styles.emptyCard}>
                            <Text style={styles.emptyTitle}>聊天暂不可用</Text>
                            <Text style={styles.emptySubtitle}>{chatErrorMessage || '请稍后再试。'}</Text>
                        </View>
                    ) : (
                        <View style={styles.chatSection}>
                            <View style={styles.chatPanel}>
                                <FlatList
                                    data={chatMessages}
                                    keyExtractor={(item) => String(item.id)}
                                    refreshControl={(
                                        <RefreshControl
                                            refreshing={refreshing}
                                            onRefresh={() => loadChat('manual')}
                                            tintColor={WECHAT.sendBtn}
                                        />
                                    )}
                                    keyboardShouldPersistTaps="handled"
                                    contentContainerStyle={chatMessages.length === 0 ? styles.chatEmptyWrap : styles.chatList}
                                    renderItem={({ item }) => {
                                        const isMine = item.senderId === user?.id;
                                        const isImage = item.messageType === MESSAGE_TYPE.IMAGE && Boolean(item.imageData);
                                        return (
                                            <View style={[styles.bubbleRow, isMine && styles.bubbleRowMine]}>
                                                <View style={[styles.bubble, isMine ? styles.bubbleMine : styles.bubbleOther]}>
                                                    {isImage && (
                                                        <Image
                                                            source={{ uri: item.imageData }}
                                                            style={styles.bubbleImage}
                                                            resizeMode="cover"
                                                        />
                                                    )}
                                                    {Boolean(item.content) && (
                                                        <Text
                                                            style={[
                                                                styles.bubbleText,
                                                                isMine && styles.bubbleTextMine,
                                                                isImage && styles.bubbleTextAfterImage,
                                                            ]}
                                                        >
                                                            {item.content}
                                                        </Text>
                                                    )}
                                                    <View style={styles.metaRow}>
                                                        <Text style={[styles.bubbleTime, isMine && styles.bubbleTimeMine]}>
                                                            {formatTime(item.createdAt)}
                                                        </Text>
                                                        {isMine && (
                                                            <Text style={[styles.readStatus, item.isRead ? styles.readStatusRead : styles.readStatusUnread]}>
                                                                {item.isRead ? '已读' : '已发送'}
                                                            </Text>
                                                        )}
                                                    </View>
                                                </View>
                                            </View>
                                        );
                                    }}
                                    ListEmptyComponent={<Text style={styles.chatEmpty}>还没有聊天记录，发一条消息吧。</Text>}
                                />
                            </View>

                            <View style={styles.inputRow}>
                                <TouchableOpacity
                                    style={[styles.imageBtn, sending && styles.imageBtnDisabled]}
                                    onPress={handlePickImage}
                                    disabled={sending}
                                >
                                    <Ionicons name="image-outline" size={20} color={sending ? '#98A4A7' : '#576B95'} />
                                </TouchableOpacity>
                                <TextInput
                                    style={styles.input}
                                    placeholder="输入消息..."
                                    placeholderTextColor="#A0A0A0"
                                    value={chatInput}
                                    onChangeText={setChatInput}
                                    maxLength={500}
                                />
                                <TouchableOpacity
                                    style={[styles.sendBtn, (!chatInput.trim() || sending) && styles.sendBtnDisabled]}
                                    onPress={handleSendChat}
                                    disabled={!chatInput.trim() || sending}
                                >
                                    <Text style={styles.sendText}>{sending ? '发送中' : '发送'}</Text>
                                </TouchableOpacity>
                            </View>
                        </View>
                    )}
                </View>
            </KeyboardAvoidingView>
        </SafeAreaView>
    );
}

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: WECHAT.page,
    },
    loadingContainer: {
        flex: 1,
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: WECHAT.page,
    },
    keyboardWrap: {
        flex: 1,
    },
    content: {
        flex: 1,
        paddingHorizontal: spacing.xl,
        paddingTop: spacing.md,
    },
    header: {
        marginBottom: spacing.md,
    },
    title: {
        fontSize: 34,
        fontWeight: '700',
        color: WECHAT.title,
        letterSpacing: 0.4,
    },
    subtitle: {
        marginTop: 4,
        fontSize: 14,
        color: WECHAT.subtitle,
    },
    requestCard: {
        backgroundColor: '#FFFFFF',
        borderRadius: radius.card,
        padding: spacing.lg,
        marginBottom: spacing.md,
        ...theme.shadowCard,
    },
    requestTitle: {
        fontSize: 15,
        fontWeight: '700',
        color: WECHAT.title,
    },
    requestDesc: {
        marginTop: 6,
        fontSize: 13,
        lineHeight: 19,
        color: WECHAT.emptySubText,
    },
    requestActionRow: {
        marginTop: spacing.md,
        flexDirection: 'row',
        gap: spacing.sm,
    },
    requestBtn: {
        flex: 1,
        height: 38,
        borderRadius: radius.md,
        alignItems: 'center',
        justifyContent: 'center',
    },
    requestRejectBtn: {
        backgroundColor: '#F4F4F4',
    },
    requestConfirmBtn: {
        backgroundColor: '#07C160',
    },
    requestRejectText: {
        color: '#333333',
        fontSize: 13,
        fontWeight: '600',
    },
    requestConfirmText: {
        color: '#FFFFFF',
        fontSize: 13,
        fontWeight: '600',
    },
    requestPending: {
        marginTop: spacing.sm,
        fontSize: 12,
        color: WECHAT.subtitle,
    },
    emptyCard: {
        backgroundColor: WECHAT.emptyCard,
        borderRadius: radius.card,
        padding: spacing.lg,
        ...theme.shadowCard,
    },
    emptyTitle: {
        fontSize: 16,
        fontWeight: '600',
        color: WECHAT.emptyText,
    },
    emptySubtitle: {
        marginTop: 6,
        fontSize: 13,
        color: WECHAT.emptySubText,
        lineHeight: 19,
    },
    chatSection: {
        flex: 1,
    },
    chatPanel: {
        flex: 1,
        backgroundColor: WECHAT.panel,
        borderRadius: 0,
        paddingVertical: spacing.md,
    },
    chatList: {
        paddingHorizontal: spacing.md,
    },
    chatEmptyWrap: {
        flexGrow: 1,
        alignItems: 'center',
        justifyContent: 'center',
        paddingHorizontal: spacing.lg,
    },
    chatEmpty: {
        fontSize: 13,
        color: WECHAT.subtitle,
    },
    bubbleRow: {
        marginBottom: spacing.sm,
        alignItems: 'flex-start',
    },
    bubbleRowMine: {
        alignItems: 'flex-end',
    },
    bubble: {
        maxWidth: '84%',
        borderRadius: 8,
        paddingHorizontal: 12,
        paddingVertical: 9,
    },
    bubbleOther: {
        backgroundColor: WECHAT.otherBubble,
        borderWidth: StyleSheet.hairlineWidth,
        borderColor: '#E6E6E6',
    },
    bubbleMine: {
        backgroundColor: WECHAT.mineBubble,
    },
    bubbleImage: {
        width: 220,
        height: 220,
        borderRadius: 8,
        backgroundColor: '#E8E8E8',
    },
    bubbleText: {
        fontSize: 14,
        color: WECHAT.otherText,
        lineHeight: 20,
    },
    bubbleTextMine: {
        color: WECHAT.mineText,
    },
    bubbleTextAfterImage: {
        marginTop: 6,
    },
    metaRow: {
        marginTop: 4,
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
    },
    bubbleTime: {
        fontSize: 10,
        color: WECHAT.time,
    },
    bubbleTimeMine: {
        color: '#5E6E51',
    },
    readStatus: {
        fontSize: 10,
        fontWeight: '600',
    },
    readStatusUnread: {
        color: '#4D5F3F',
    },
    readStatusRead: {
        color: '#6E6E6E',
    },
    inputRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: spacing.sm,
        backgroundColor: WECHAT.inputBar,
        borderTopWidth: StyleSheet.hairlineWidth,
        borderTopColor: WECHAT.divider,
        paddingHorizontal: spacing.sm,
        paddingTop: spacing.sm,
        paddingBottom: spacing.sm,
    },
    imageBtn: {
        width: COMPOSER_HEIGHT,
        height: COMPOSER_HEIGHT,
        borderRadius: 9,
        borderWidth: 1,
        borderColor: WECHAT.inputBorder,
        backgroundColor: WECHAT.inputBg,
        alignItems: 'center',
        justifyContent: 'center',
    },
    imageBtnDisabled: {
        opacity: 0.6,
    },
    input: {
        flex: 1,
        height: COMPOSER_HEIGHT,
        backgroundColor: WECHAT.inputBg,
        borderRadius: 9,
        paddingHorizontal: 12,
        paddingVertical: 0,
        borderWidth: 1,
        borderColor: WECHAT.inputBorder,
        color: WECHAT.title,
    },
    sendBtn: {
        minWidth: 72,
        height: COMPOSER_HEIGHT,
        borderRadius: 9,
        backgroundColor: WECHAT.sendBtn,
        alignItems: 'center',
        justifyContent: 'center',
        paddingHorizontal: 12,
    },
    sendBtnDisabled: {
        backgroundColor: '#9FDBBA',
    },
    sendText: {
        color: WECHAT.sendBtnText,
        fontWeight: '600',
        fontSize: 14,
    },
});
